import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useSelector } from "react-redux";

import { ApiManager, Locale, Request } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import Base from "../Base";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const base = Base.getInstance();

const STATUS = {
  CREATED: 0,
  GENERATING_CODES: 1,
  GENERATED_CODES: 2,
  GENERATING_EXCEL: 3,
  GENERATED_EXCEL: 4,
};

function statusTag(record) {
  const s = record.status;
  let key = "member.giftCodes.statusUnknown";
  let color = "default";
  if (s === STATUS.CREATED) {
    key = "member.giftCodes.statusCreated";
    color = "blue";
  } else if (s === STATUS.GENERATING_CODES) {
    key = "member.giftCodes.statusGeneratingCodes";
    color = "orange";
  } else if (s === STATUS.GENERATED_CODES) {
    key = "member.giftCodes.statusGeneratedCodes";
    color = "cyan";
  } else if (s === STATUS.GENERATING_EXCEL) {
    key = "member.giftCodes.statusGeneratingExcel";
    color = "orange";
  } else if (s >= STATUS.GENERATED_EXCEL) {
    key = "member.giftCodes.statusGeneratedExcel";
    color = "success";
  }
  return (
    <Tag color={color}>{i18n.t(key)}</Tag>
  );
}

function buildCodesUrl(activityId, { page, pageSize, keyword, redeemed }) {
  let qs = `count=${pageSize}&page=${page}`;
  if (keyword && keyword.trim()) {
    qs += `&filter[keyword]=${encodeURIComponent(keyword.trim())}`;
  }
  if (redeemed === 0 || redeemed === 1) {
    qs += `&filter[redeemed]=${redeemed}`;
  }
  return `/member/api/v6/merchant/member-gift-activity/${activityId}/codes?${qs}`;
}

function MemberGiftActivities() {
  const user = useSelector((state) => getUserInfo(state));
  const [form] = Form.useForm();
  const [listLoading, setListLoading] = useState(false);
  const [listPayload, setListPayload] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [cards, setCards] = useState([]);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [codesDrawerOpen, setCodesDrawerOpen] = useState(false);
  const [codesActivityId, setCodesActivityId] = useState(null);
  const [codesBatchLabel, setCodesBatchLabel] = useState("");
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesPayload, setCodesPayload] = useState(null);
  const [codesPage, setCodesPage] = useState(1);
  const [codesPageSize, setCodesPageSize] = useState(10);
  const [codesKeyword, setCodesKeyword] = useState("");
  const [codesRedeemed, setCodesRedeemed] = useState(undefined);

  const pollTimer = useRef(null);

  const loadCards = useCallback(async () => {
    if (!user?.token) return;
    try {
      const url = "/member/api/v6/merchant/member-card";
      const res = await Request.get(url, user.token);
      if (res.result && res.data?.data) {
        setCards(res.data.data);
      }
    } catch (e) {
      message.error(e.message);
    }
  }, [user?.token]);

  const loadActivities = useCallback(async () => {
    if (!user?.token) return;
    setListLoading(true);
    try {
      const res = await api.member.giftActivityList.run({
        token: user.token,
        query: { count: pageSize, page },
      });
      if (res.result) {
        setListPayload(res.data);
      } else {
        message.warning(res.message);
      }
    } catch (e) {
      base.handleApiError(api.member.giftActivityList, e);
    } finally {
      setListLoading(false);
    }
  }, [user?.token, page, pageSize]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  const openCreateModal = () => {
    setEditingId(null);
    form.resetFields();
    setActivityModalOpen(true);
  };

  const openEditModal = (record) => {
    if (record.status !== STATUS.CREATED) {
      message.info(i18n.t("member.giftCodes.onlyDraftEditable"));
      return;
    }
    setEditingId(record.id);
    form.setFieldsValue({
      member_card_id: record.member_card_id,
      batch_number: record.batch_number,
      quantity: record.quantity,
      zh_name: record.zh_name,
      ug_name: record.ug_name,
    });
    setActivityModalOpen(true);
  };

  const submitActivityForm = () =>
    form.validateFields().then(async (values) => {
      if (!user?.token) return;
      setSubmitting(true);
      try {
        const payload = {
          member_card_id: values.member_card_id,
          batch_number: values.batch_number,
          quantity: values.quantity,
          zh_name: values.zh_name || "",
          ug_name: values.ug_name || "",
        };
        let res;
        if (editingId) {
          res = await api.member.giftActivityUpdate.run({
            token: user.token,
            path: { id: editingId },
            data: payload,
          });
        } else {
          res = await api.member.giftActivityCreate.run({
            token: user.token,
            data: payload,
          });
        }
        if (!res.result) {
          message.warning(res.message);
          throw Object.assign(new Error("biz"), { skipApiHandler: true });
        }
        message.success(res.message || i18n.t("member.giftCodes.saveSuccess"));
        setActivityModalOpen(false);
        loadActivities();
      } catch (e) {
        if (!e.skipApiHandler) {
          base.handleApiError(
            editingId ? api.member.giftActivityUpdate : api.member.giftActivityCreate,
            e
          );
        }
        throw e;
      } finally {
        setSubmitting(false);
      }
    });

  const deleteActivity = (record) => {
    if (record.status !== STATUS.CREATED) {
      message.info(i18n.t("member.giftCodes.onlyDraftDeletable"));
      return;
    }
    base.showModal(
      i18n.t("member.giftCodes.deleteConfirm"),
      Base.Modal.confirm,
      async () => {
        try {
          const res = await api.member.giftActivityDelete.run({
            token: user.token,
            path: { id: record.id },
          });
          if (res.result) {
            message.success(res.message || i18n.t("member.giftCodes.deleteSuccess"));
            loadActivities();
          } else {
            message.warning(res.message);
          }
        } catch (e) {
          base.handleApiError(api.member.giftActivityDelete, e);
        }
      }
    );
  };

  const postJob = async (record, apiMethod, confirmKey) => {
    if (!user?.token) return;
    base.showModal(i18n.t(confirmKey), Base.Modal.confirm, async () => {
      try {
        const res = await apiMethod.run({
          token: user.token,
          path: { id: record.id },
          data: {},
        });
        if (res.result) {
          message.success(res.message || i18n.t("member.giftCodes.jobQueued"));
          loadActivities();
        } else {
          message.warning(res.message);
        }
      } catch (e) {
        base.handleApiError(apiMethod, e);
      }
    });
  };

  const loadCodes = useCallback(async () => {
    if (!user?.token || !codesActivityId) return;
    setCodesLoading(true);
    try {
      const url = buildCodesUrl(codesActivityId, {
        page: codesPage,
        pageSize: codesPageSize,
        keyword: codesKeyword,
        redeemed: codesRedeemed,
      });
      const res = await Request.get(url, user.token);
      if (res.result) {
        setCodesPayload(res.data);
      } else {
        message.warning(res.message);
      }
    } catch (e) {
      message.error(e.message);
    } finally {
      setCodesLoading(false);
    }
  }, [
    user?.token,
    codesActivityId,
    codesPage,
    codesPageSize,
    codesKeyword,
    codesRedeemed,
  ]);

  useEffect(() => {
    if (codesDrawerOpen && codesActivityId) {
      loadCodes();
    }
  }, [codesDrawerOpen, codesActivityId, loadCodes]);

  const openCodesDrawer = (record) => {
    setCodesActivityId(record.id);
    setCodesBatchLabel(record.batch_number || `#${record.id}`);
    setCodesPage(1);
    setCodesKeyword("");
    setCodesRedeemed(undefined);
    setCodesDrawerOpen(true);
  };

  const startPollingList = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(() => {
      loadActivities();
    }, 5000);
    message.info(i18n.t("member.giftCodes.pollStarted"));
  };

  const stopPollingList = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    message.info(i18n.t("member.giftCodes.pollStopped"));
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 72,
      fixed: "left",
    },
    {
      title: i18n.t("member.giftCodes.batchNumber"),
      dataIndex: "batch_number",
      width: 120,
    },
    {
      title: i18n.t("agentLevel.zh"),
      dataIndex: "zh_name",
      ellipsis: true,
      width: 140,
    },
    {
      title: i18n.t("agentLevel.ug"),
      dataIndex: "ug_name",
      ellipsis: true,
      width: 140,
      render: (text) => (
        <Typography.Text style={{ fontFamily: "Alp Ekran" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: i18n.t("member.giftCodes.memberCard"),
      width: 160,
      render: (_, record) =>
        record.member_card ? (
          <Tag>{i18n.getPropNameByLocale(record.member_card)}</Tag>
        ) : (
          "—"
        ),
    },
    {
      title: i18n.t("member.giftCodes.quantity"),
      dataIndex: "quantity",
      width: 88,
    },
    {
      title: i18n.t("member.giftCodes.status"),
      width: 140,
      render: (_, record) => statusTag(record),
    },
    {
      title: i18n.t("member.giftCodes.export"),
      width: 200,
      render: (_, record) =>
        record.excel_path ? (
          <Typography.Link href={record.excel_path} target="_blank" rel="noreferrer">
            {i18n.t("member.giftCodes.downloadExcel")}
          </Typography.Link>
        ) : (
          "—"
        ),
    },
    {
      title: i18n.t("product.cats.actions"),
      fixed: "right",
      width: 380,
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            {i18n.t("product.productItem.actionEdit")}
          </Button>
          <Button type="link" size="small" danger onClick={() => deleteActivity(record)}>
            {i18n.t("product.productItem.actionDelete")}
          </Button>
          <Button
            type="link"
            size="small"
            disabled={record.status !== STATUS.CREATED}
            onClick={() =>
              postJob(
                record,
                api.member.giftActivityGenerateCodes,
                "member.giftCodes.confirmGenerateCodes"
              )
            }
          >
            {i18n.t("member.giftCodes.generateCodes")}
          </Button>
          <Button
            type="link"
            size="small"
            disabled={record.status !== STATUS.GENERATED_CODES}
            onClick={() =>
              postJob(
                record,
                api.member.giftActivityGenerateExcel,
                "member.giftCodes.confirmGenerateExcel"
              )
            }
          >
            {i18n.t("member.giftCodes.generateExcel")}
          </Button>
          <Button type="link" size="small" onClick={() => openCodesDrawer(record)}>
            {i18n.t("member.giftCodes.viewCodes")}
          </Button>
        </Space>
      ),
    },
  ];

  const codesColumns = [
    {
      title: i18n.t("member.giftCodes.codeValue"),
      dataIndex: "code",
      ellipsis: true,
    },
    {
      title: i18n.t("member.giftCodes.userId"),
      dataIndex: "user_id",
      width: 100,
      render: (v) => v ?? "—",
    },
    {
      title: i18n.t("member.giftCodes.redeemedAt"),
      dataIndex: "redeemed_at",
      width: 180,
      render: (v) => v ?? "—",
    },
  ];

  const tableData = listPayload?.data ?? [];
  const codesRows = codesPayload?.data ?? [];

  return (
    <div className="route-member-gift-activities">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Typography.Title level={4} style={{ margin: 0 }}>
          {i18n.t("member.giftCodes.title")}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {i18n.t("member.giftCodes.docReference")}
        </Typography.Paragraph>
        <Space wrap>
          <Button type="primary" onClick={openCreateModal}>
            {i18n.t("member.giftCodes.createActivity")}
          </Button>
          <Button onClick={() => loadActivities()}>{i18n.t("member.giftCodes.refreshList")}</Button>
          <Button onClick={startPollingList}>{i18n.t("member.giftCodes.autoRefresh")}</Button>
          <Button onClick={stopPollingList}>{i18n.t("member.giftCodes.stopAutoRefresh")}</Button>
        </Space>
        <Table
          loading={listLoading}
          rowKey={(r) => `gift-act-${r.id}`}
          columns={columns}
          dataSource={tableData}
          scroll={{ x: 1100 }}
          pagination={{
            current: listPayload?.current_page ?? page,
            pageSize: listPayload?.per_page ?? pageSize,
            total: listPayload?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Space>

      <Drawer
        title={`${i18n.t("member.giftCodes.codesTitle")} — ${codesBatchLabel}`}
        width={720}
        open={codesDrawerOpen}
        onClose={() => {
          setCodesDrawerOpen(false);
          setCodesActivityId(null);
        }}
        destroyOnClose
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            placeholder={i18n.t("member.giftCodes.filterKeyword")}
            value={codesKeyword}
            onChange={(e) => setCodesKeyword(e.target.value)}
            onPressEnter={() => {
              setCodesPage(1);
              loadCodes();
            }}
            style={{ width: 200 }}
          />
          <Select
            allowClear
            placeholder={i18n.t("member.giftCodes.filterRedeemed")}
            style={{ width: 160 }}
            value={codesRedeemed}
            onChange={(v) => setCodesRedeemed(v)}
            options={[
              { value: 1, label: i18n.t("member.giftCodes.filterRedeemedYes") },
              { value: 0, label: i18n.t("member.giftCodes.filterRedeemedNo") },
            ]}
          />
          <Button
            type="primary"
            onClick={() => {
              setCodesPage(1);
              loadCodes();
            }}
          >
            {i18n.t("member.giftCodes.applyFilters")}
          </Button>
        </Space>
        <Table
          loading={codesLoading}
          rowKey={(r) => `code-${r.id}`}
          columns={codesColumns}
          dataSource={codesRows}
          pagination={{
            current: codesPayload?.current_page ?? codesPage,
            pageSize: codesPayload?.per_page ?? codesPageSize,
            total: codesPayload?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setCodesPage(p);
              setCodesPageSize(ps);
            },
          }}
        />
      </Drawer>

      <Modal
        title={
          editingId
            ? i18n.t("member.giftCodes.editActivity")
            : i18n.t("member.giftCodes.createActivity")
        }
        open={activityModalOpen}
        onCancel={() => setActivityModalOpen(false)}
        onOk={() => submitActivityForm()}
        confirmLoading={submitting}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="member_card_id"
            label={i18n.t("member.giftCodes.memberCard")}
            rules={[{ required: true, message: i18n.t("member.cardWarning") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={cards.map((c) => ({
                value: c.id,
                label: i18n.getPropNameByLocale(c),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="batch_number"
            label={i18n.t("member.giftCodes.batchNumber")}
            rules={[{ required: true, message: i18n.t("member.giftCodes.batchNumberRequired") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="quantity"
            label={i18n.t("member.giftCodes.quantity")}
            rules={[{ required: true, message: i18n.t("member.giftCodes.quantityRequired") }]}
          >
            <InputNumber min={1} step={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="zh_name" label={i18n.t("agentLevel.zh")}>
            <Input />
          </Form.Item>
          <Form.Item name="ug_name" label={i18n.t("agentLevel.ug")}>
            <Input style={{ fontFamily: "Alp Ekran" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default MemberGiftActivities;
